// src/pages/DashboardPage.js – Cinematic enhanced
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { canClaimDaily } from '../utils/helpers';
import LevelUpOverlay from '../components/LevelUpOverlay';
import Chat from '../components/Chat';
import { useSound } from '../hooks/useSound';
import { useCinematic } from '../hooks/useCinematic';
import useViewport from '../hooks/useViewport';

const ALL_BADGES = [
  { id:'welcome',      name:'Welcome!',      icon:'🎉' },
  { id:'html_beginner',name:'HTML Beginner', icon:'🌐' },
  { id:'css_beginner', name:'CSS Beginner',  icon:'🎨' },
  { id:'js_beginner',  name:'JS Beginner',   icon:'⚡' },
  { id:'html_master',  name:'HTML Master',   icon:'🏆' },
  { id:'css_master',   name:'CSS Master',    icon:'✨' },
  { id:'js_master',    name:'JS Master',     icon:'🔱' },
];

const QUICK = [
  { label:'🎯 Start Mission', to:'/missions',    color:'#00f5ff' },
  { label:'🌍 3D World',      to:'/world',       color:'#39ff14' },
  { label:'🏆 Leaderboard',   to:'/leaderboard', color:'#ffd700' },
  { label:'🏅 Achievements',  to:'/achievements',color:'#bf5fff' },
  { label:'🏪 Shop',          to:'/shop',        color:'#ff6432' },
];

function hexToRgba(hex, a) {
  if (!hex || !hex.startsWith('#')) return `rgba(0,245,255,${a})`;
  const r=parseInt(hex.slice(1,3),16), g=parseInt(hex.slice(3,5),16), b=parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
}

export default function DashboardPage() {
  const { user, claimDailyReward, showToast } = useAuth();
  const { onlineCount } = useSocket();
  const navigate = useNavigate();
  const [levelUp, setLevelUp] = useState(null);
  const closeLevelUp = useCallback(() => setLevelUp(null), []);
  const [claiming, setClaiming] = useState(false);
  const { playClick, playCoin, playSuccess, playHover } = useSound();
  const { spawnButtonSparks, glowFlash, screenShake } = useCinematic();
  const { isMobile } = useViewport();

  if (!user) return null;

  const xpPct   = Math.min(((user.xp - (user.level-1)*500) / 500) * 100, 100);
  const total   = (user.completedMissions || []).length;
  const canClaim = canClaimDaily(user.lastDailyReward);

  const handleDaily = async (e) => {
    if (claiming || !canClaim) return;
    playClick(); spawnButtonSparks(e, '#ffd700');
    setClaiming(true);
    try {
      const data = await claimDailyReward();
      playCoin(); glowFlash('rgba(255,215,0,0.14)', 500);
      const newLevel = Math.floor(data.newXP / 500) + 1;
      if (newLevel > user.level) setLevelUp(newLevel);
    } catch {
      showToast('Come back tomorrow!', 'Daily reward already claimed', 'gold');
    } finally { setClaiming(false); }
  };

  const handleQuickNav = (e, to) => {
    playClick(); spawnButtonSparks(e, '#00f5ff');
    navigate(to);
  };

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden', background:'#0a0a0f', position:'relative' }}>
      {levelUp && <LevelUpOverlay level={levelUp} onClose={closeLevelUp} />}

      <div className="page-shell" style={{ flex:1, display:'flex', flexDirection:'column', gap:'22px', paddingBottom:'60px' }}>

        {/* Hero */}
        <div className="animate-fade-in" style={{
          background:'linear-gradient(135deg,rgba(0,245,255,0.07),rgba(124,58,237,0.07))',
          border:'1px solid rgba(0,245,255,0.14)', borderRadius:'14px', padding:isMobile ? '18px 16px' : '24px 28px',
          display:'flex', alignItems:isMobile ? 'flex-start' : 'center', justifyContent:'space-between', gap:'16px', flexWrap:isMobile ? 'wrap' : 'nowrap',
          backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)',
          boxShadow:'0 0 40px rgba(0,245,255,0.04)',
        }}>
          <div>
            <div style={{ fontFamily:'var(--font-game)', fontSize:isMobile ? '16px' : '20px', fontWeight:700, color:'#fff', marginBottom:'5px' }}>
              WELCOME BACK, <span style={{ color:'#00f5ff', textShadow:'0 0 12px rgba(0,245,255,0.5)' }}>{user.username.toUpperCase()}</span>
            </div>
            <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'15px', marginBottom:'14px', lineHeight:1.5 }}>
              🔥 {user.streak || 0} day streak &nbsp;·&nbsp; {total} missions done &nbsp;·&nbsp;
              <span style={{ color:'#39ff14', textShadow:'0 0 8px rgba(57,255,20,0.4)' }}>{onlineCount} online</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', maxWidth:isMobile ? '100%' : '240px' }}>
              <span style={{ fontFamily:'var(--font-game)', fontSize:'15px', color:'#00f5ff', letterSpacing:'1px', flexShrink:0 }}>LVL {user.level}</span>
              <div className="xp-bar" style={{ flex:1, height:'8px' }}>
                <div className="xp-fill" style={{ width:`${xpPct}%` }} />
              </div>
              <span style={{ fontFamily:'var(--font-game)', fontSize:'15px', color:'rgba(255,255,255,0.3)', flexShrink:0 }}>{user.xp}/{user.level*500}</span>
            </div>
          </div>
          <div style={{ textAlign:'center', flexShrink:0, width:isMobile ? '100%' : 'auto' }}>
            <div style={{ fontSize:'44px', marginBottom:'8px', filter:'drop-shadow(0 0 12px rgba(0,245,255,0.3))' }}>{user.avatar || '🧑‍💻'}</div>
            <button onClick={handleDaily} disabled={!canClaim || claiming} style={{
              padding:'10px 18px',
              background: canClaim ? 'linear-gradient(135deg,rgba(255,215,0,0.22),rgba(255,140,0,0.18))' : 'rgba(255,255,255,0.04)',
              border:`1px solid ${canClaim ? 'rgba(255,215,0,0.45)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius:'7px', color: canClaim ? '#ffd700' : 'rgba(255,255,255,0.25)',
              cursor: canClaim ? 'pointer' : 'not-allowed',
              fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, letterSpacing:'1px',
              transition:'all 0.22s',
              boxShadow: canClaim ? '0 0 18px rgba(255,215,0,0.15)' : 'none',
            }}
              onMouseEnter={e => { if(canClaim){ playHover(); e.currentTarget.style.boxShadow='0 0 26px rgba(255,215,0,0.28)'; e.currentTarget.style.transform='scale(1.04)'; }}}
              onMouseLeave={e => { e.currentTarget.style.boxShadow=canClaim?'0 0 18px rgba(255,215,0,0.15)':'none'; e.currentTarget.style.transform=''; }}
            >
              {claiming ? '...' : canClaim ? '⚡ DAILY REWARD' : '✓ CLAIMED'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid-4 animate-fade-in">
          {[
            { val:user.level,  label:'LEVEL',    color:'#00f5ff' },
            { val:user.xp,     label:'TOTAL XP', color:'#7c3aed' },
            { val:user.coins,  label:'COINS',    color:'#ffd700' },
            { val:total,       label:'MISSIONS', color:'#39ff14' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div style={{ fontFamily:'var(--font-game)', fontSize:'24px', fontWeight:700, color:s.color,
                            textShadow:`0 0 12px ${hexToRgba(s.color, 0.4)}` }}>{s.val}</div>
              <div className="stat-lbl">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Badges */}
        <div className="animate-fade-in">
          <div className="section-title">BADGES</div>
          <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
            {ALL_BADGES.map(b => {
              const owned = (user.badges || []).some(ub => ub.id === b.id);
              return (
                <div key={b.id} style={{
                  display:'inline-flex', alignItems:'center', gap:'7px',
                  padding:'6px 13px', borderRadius:'20px', fontSize:'15px', fontWeight:700,
                  background: owned ? 'rgba(255,215,0,0.08)' : 'rgba(255,255,255,0.03)',
                  border:`1px solid ${owned ? 'rgba(255,215,0,0.28)' : 'rgba(255,255,255,0.06)'}`,
                  color: owned ? '#ffd700' : 'rgba(255,255,255,0.2)',
                  filter: owned ? 'none' : 'grayscale(1)',
                  boxShadow: owned ? '0 0 14px rgba(255,215,0,0.1)' : 'none',
                  transition:'all 0.22s',
                }}>
                  <span style={{ fontSize:'16px' }}>{b.icon}</span>{b.name}
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="animate-fade-in">
          <div className="section-title">QUICK ACTIONS</div>
          <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
            {QUICK.map(q => (
              <button key={q.to} onClick={e => handleQuickNav(e, q.to)} style={{
                padding:'11px 20px', background:'rgba(255,255,255,0.03)',
                border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px',
                color:q.color, cursor:'pointer', fontFamily:'var(--font-game)',
                fontSize:'15px', fontWeight:700, letterSpacing:'1px', transition:'all 0.2s',
              }}
                onMouseEnter={e => { playHover(); e.currentTarget.style.background='rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.16)'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 0 16px ${hexToRgba(q.color,0.18)}`; }}
                onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.08)'; e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}
              >{q.label}</button>
            ))}
          </div>
        </div>
      </div>
      <Chat />
    </div>
  );
}
